import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/auth/useAuth'
import { cartApi } from '@/api/cart'
import { trackEvent } from '@/tracking/track'
import type { CartItem } from '@/types'

const GUEST_CART_KEY = 'zenzers-cart'

interface CartContextType {
  items: CartItem[]
  count: number
  addItem: (productId: string, quantity?: number) => void
  updateItem: (productId: string, quantity: number) => void
  removeItem: (productId: string) => void
  clearCart: () => void
  loading: boolean
}

const CartContext = createContext<CartContextType>({
  items: [],
  count: 0,
  addItem: () => {},
  updateItem: () => {},
  removeItem: () => {},
  clearCart: () => {},
  loading: false,
})

export function useCart() {
  return useContext(CartContext)
}

function getGuestCart(): CartItem[] {
  try {
    return JSON.parse(localStorage.getItem(GUEST_CART_KEY) || '[]')
  } catch {
    return []
  }
}

function setGuestCart(items: CartItem[]) {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items))
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, getAccessToken } = useAuth()
  const [items, setItems] = useState<CartItem[]>([])
  const [serverItemMap, setServerItemMap] = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(false)
  const [merged, setMerged] = useState(false)

  // Load guest cart on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setItems(getGuestCart())
    }
  }, [isAuthenticated])

  // When user authenticates, merge guest cart and load server cart
  useEffect(() => {
    if (!isAuthenticated || !getAccessToken() || merged) return

    const mergeAndLoad = async () => {
      setLoading(true)
      try {
        const guestItems = getGuestCart()
        if (guestItems.length > 0) {
          await cartApi.mergeCart(guestItems)
          localStorage.removeItem(GUEST_CART_KEY)
        }

        const cart = await cartApi.getCart()
        const idMap = new Map<string, string>()
        const cartItems: CartItem[] = (cart.items || []).map((item: any) => {
          idMap.set(item.productId, item._id)
          return { productId: item.productId, quantity: item.quantity }
        })
        setServerItemMap(idMap)
        setItems(cartItems)
        setMerged(true)
      } catch (err) {
        // A 401 here is benign — the storefront session simply isn't authorized for the
        // server cart yet (the guest localStorage cart still works). Don't surface it as a
        // console error; it was being mistaken for a real failure.
        const status = (err as { status?: number })?.status
        if (status !== 401) console.error('Failed to load cart:', err)
      } finally {
        setLoading(false)
      }
    }

    mergeAndLoad()
  }, [isAuthenticated, getAccessToken, merged])

  const addItem = useCallback(
    async (productId: string, quantity = 1) => {
      trackEvent('add_to_cart', { item_id: productId, quantity })
      if (isAuthenticated) {
        try {
          const cart = await cartApi.addItem(productId, quantity)
          const idMap = new Map<string, string>()
          const cartItems: CartItem[] = (cart.items || []).map((item: any) => {
            idMap.set(item.productId, item._id)
            return { productId: item.productId, quantity: item.quantity }
          })
          setServerItemMap(idMap)
          setItems(cartItems)
        } catch (err) {
          console.error('Failed to add item:', err)
        }
      } else {
        setItems((prev) => {
          const existing = prev.find((i) => i.productId === productId)
          const updated = existing
            ? prev.map((i) => (i.productId === productId ? { ...i, quantity: i.quantity + quantity } : i))
            : [...prev, { productId, quantity }]
          setGuestCart(updated)
          return updated
        })
      }
    },
    [isAuthenticated],
  )

  const updateItem = useCallback(
    async (productId: string, quantity: number) => {
      if (isAuthenticated) {
        const serverId = serverItemMap.get(productId)
        if (!serverId) return
        try {
          const cart = await cartApi.updateItem(serverId, quantity)
          const idMap = new Map<string, string>()
          const cartItems: CartItem[] = (cart.items || []).map((item: any) => {
            idMap.set(item.productId, item._id)
            return { productId: item.productId, quantity: item.quantity }
          })
          setServerItemMap(idMap)
          setItems(cartItems)
        } catch (err) {
          console.error('Failed to update item:', err)
        }
      } else {
        setItems((prev) => {
          const updated = prev.map((i) => (i.productId === productId ? { ...i, quantity } : i))
          setGuestCart(updated)
          return updated
        })
      }
    },
    [isAuthenticated, serverItemMap],
  )

  const removeItem = useCallback(
    async (productId: string) => {
      if (isAuthenticated) {
        const serverId = serverItemMap.get(productId)
        if (!serverId) return
        try {
          const cart = await cartApi.removeItem(serverId)
          const idMap = new Map<string, string>()
          const cartItems: CartItem[] = (cart.items || []).map((item: any) => {
            idMap.set(item.productId, item._id)
            return { productId: item.productId, quantity: item.quantity }
          })
          setServerItemMap(idMap)
          setItems(cartItems)
        } catch (err) {
          console.error('Failed to remove item:', err)
        }
      } else {
        setItems((prev) => {
          const updated = prev.filter((i) => i.productId !== productId)
          setGuestCart(updated)
          return updated
        })
      }
    },
    [isAuthenticated, serverItemMap],
  )

  const clearCart = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await cartApi.clearCart()
      } catch (err) {
        console.error('Failed to clear cart:', err)
      }
    } else {
      localStorage.removeItem(GUEST_CART_KEY)
    }
    setItems([])
    setServerItemMap(new Map())
  }, [isAuthenticated])

  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items])

  const value = useMemo(
    () => ({ items, count, addItem, updateItem, removeItem, clearCart, loading }),
    [items, count, addItem, updateItem, removeItem, clearCart, loading],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}
